// src/hooks/useSavedScans.js
// SCREENER-FRAG — new file. Logic extracted from ScreenerPage.jsx (previously
// inline state + handlers: loadSavedScans/loadAlerts/handleSaveScan/
// handleLoadScan/handleToggleAlert/handleDeleteScan). Content of each handler
// is unchanged; only the boundary changed — this hook owns saved-scan/alert
// data and CRUD, the parent (ScreenerPage) still owns which filters are
// currently active and how to apply a loaded scan to them.
//
// WHY THIS BOUNDARY: SavedScansPanel needs to know nothing about signal/
// sector/sort state, and useScreenerData needs to know nothing about saved
// scans — each can change without the other breaking, which is the whole
// point of the fragmentation.
//
// onLoadScan(scan) — required callback. Called after the "touch" API call
// succeeds, so the parent can apply scan.filters to its own state, trigger
// fetchResults, and set savedScanUsedRef for D3 signal logging. This hook
// deliberately does not reach into the parent's filter state or refs.
//
// onAlertsCountChange(count) — optional callback, fired whenever the alerts
// array changes length, so the parent's signal-log payload (alertsActive)
// stays current without lifting the full alerts array up.

import { useState, useEffect, useCallback } from 'react';
import apiFetch from '../utils/apiFetch';
import { API_URL } from '../constants/screenerConfig';

export function useSavedScans({ isLoggedIn, onLoadScan, onAlertsCountChange }) {
  const [savedScans,    setSavedScans]    = useState([]);
  const [alerts,        setAlerts]        = useState([]);
  const [showLibrary,   setShowLibrary]   = useState(false);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saveName,      setSaveName]      = useState('');
  const [saveLoading,   setSaveLoading]   = useState(false);
  const [saveError,     setSaveError]     = useState('');
  const [alertLoading,  setAlertLoading]  = useState(null);
  const [scanToDelete,  setScanToDelete]  = useState(null);

  const loadSavedScans = useCallback(async () => {
    try {
      const res  = await apiFetch(`${API_URL}/api/screener/saved-scans`);
      const data = await res.json();
      if (data.scans) setSavedScans(data.scans);
    } catch (e) { /* non-blocking */ }
  }, []);

  const loadAlerts = useCallback(async () => {
    try {
      const res  = await apiFetch(`${API_URL}/api/screener/alerts`);
      const data = await res.json();
      if (data.alerts) setAlerts(data.alerts);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    loadSavedScans();
    loadAlerts();
  }, [isLoggedIn, loadSavedScans, loadAlerts]);

  useEffect(() => {
    onAlertsCountChange?.(alerts.length);
  }, [alerts.length, onAlertsCountChange]);

  async function handleSaveScan(currentFilters) {
    if (!saveName.trim()) { setSaveError('Please enter a name.'); return; }
    setSaveLoading(true);
    setSaveError('');
    try {
      const res  = await apiFetch(`${API_URL}/api/screener/saved-scans`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ name: saveName.trim(), filters: currentFilters }),
      });
      const data = await res.json();
      if (!res.ok) { setSaveError(data.error || 'Could not save.'); return; }
      setSavedScans(prev => [data.scan, ...prev]);
      setSaveName('');
      setShowSaveInput(false);
      setShowLibrary(true);
    } catch (e) {
      setSaveError('Could not save scan. Try again.');
    } finally {
      setSaveLoading(false);
    }
  }

  async function handleLoadScan(scan) {
    try {
      await apiFetch(`${API_URL}/api/screener/saved-scans/${scan._id}/touch`, { method: 'PATCH' });
    } catch (e) { /* non-blocking */ }
    onLoadScan?.(scan);
  }

  async function handleToggleAlert(scan) {
    const existingAlert = alerts.find(a => String(a.savedScanId) === String(scan._id));
    setAlertLoading(scan._id);
    try {
      if (existingAlert) {
        const res = await apiFetch(`${API_URL}/api/screener/alerts/${existingAlert._id}`, { method: 'DELETE' });
        if (res.ok) setAlerts(prev => prev.filter(a => a._id !== existingAlert._id));
      } else {
        const res  = await apiFetch(`${API_URL}/api/screener/alerts`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ savedScanId: scan._id }),
        });
        const data = await res.json();
        if (res.ok && data.alert) setAlerts(prev => [data.alert, ...prev]);
        else if (!res.ok) alert(data.error || 'Could not create alert.');
      }
    } catch (e) { /* non-blocking */ } finally {
      setAlertLoading(null);
    }
  }

  async function handleDeleteScan(scan) {
    try {
      const res = await apiFetch(`${API_URL}/api/screener/saved-scans/${scan._id}`, { method: 'DELETE' });
      if (res.ok) {
        setSavedScans(prev => prev.filter(s => s._id !== scan._id));
        setAlerts(prev => prev.filter(a => String(a.savedScanId) !== String(scan._id)));
      }
    } catch (e) { /* non-blocking */ } finally {
      setScanToDelete(null);
    }
  }

  return {
    savedScans, alerts, showLibrary, setShowLibrary,
    showSaveInput, setShowSaveInput, saveName, setSaveName,
    saveLoading, saveError, setSaveError, alertLoading, scanToDelete, setScanToDelete,
    handleSaveScan, handleLoadScan, handleToggleAlert, handleDeleteScan,
  };
}
